import {
  canonicalBarToIbkrRawBar,
  toCandles,
} from '../candleStore.js'
import { subscribeRuntimeBars } from './runtimeFanout.js'

export const EQUITY_STREAM_TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min', '65min', 'daily'])

export const EQUITY_BAR_SIZE_BY_TIMEFRAME = Object.freeze({
  '1min': '1min',
  '2min': '2min',
  '5min': '5min',
  '15min': '15min',
  '30min': '30min',
  '65min': '65min',
  daily: '1day',
})

const IBKR_BAR_SIZE_BY_EQUITY_BAR_SIZE = Object.freeze({
  '1min': '1 min',
  '2min': '2 mins',
  '5min': '5 mins',
  '15min': '15 mins',
  '30min': '30 mins',
  '65min': '65 mins',
  '1day': '1 day',
})

const EQUITY_BAR_SIZE_SECONDS = Object.freeze({
  '1min': 60,
  '2min': 120,
  '5min': 300,
  '15min': 900,
  '30min': 1800,
  '65min': 3900,
  '1day': 86400,
})

export function subscribeEquityStream({
  symbol,
  timeframe,
  timezone,
  session = 'regular',
  onBar,
  onLastPrice,
  onStatus,
  onError,
  ...runtimeOptions
} = {}) {
  const barSize = equityStreamBarSizeForTimeframe(timeframe)
  if (!barSize) return null

  return subscribeRuntimeBars({
    symbol,
    barSize,
    session,
    ...runtimeOptions,
    onBar: (payload) => {
      const chartBar = equityStreamBarToChartBar(payload, timeframe, timezone)
      if (!chartBar) return
      if (typeof onBar === 'function') onBar({ chartBar, payload })
    },
    onLastPrice: (payload) => {
      const chartBar = equityStreamLastPriceToChartBar(payload, timeframe, timezone)
      if (!chartBar) return
      if (typeof onLastPrice === 'function') onLastPrice({ chartBar, payload })
    },
    onStatus,
    onError,
  })
}

export function equityStreamBarSizeForTimeframe(timeframe) {
  return EQUITY_BAR_SIZE_BY_TIMEFRAME[timeframe] || null
}

export function equityStreamBarToChartBar(payload, timeframe, timezone) {
  const barSize = equityStreamBarSizeForTimeframe(timeframe)
  const ibkrBarSize = IBKR_BAR_SIZE_BY_EQUITY_BAR_SIZE[barSize]
  if (!barSize || !ibkrBarSize) return null
  if (payload?.barSize !== barSize) return null

  const rawBar = canonicalBarToIbkrRawBar({
    barTime: payload.barTime,
    open: payload.open,
    high: payload.high,
    low: payload.low,
    close: payload.close,
    volume: payload.volume,
  }, ibkrBarSize)
  if (!rawBar) return null
  const candle = toCandles([rawBar], timezone)[0]
  if (!candle) return null

  return {
    time: candle.time,
    rawEpochMs: candle.rawEpochMs,
    eventEpochMs: finitePositiveNumber(payload.eventTs)
      ?? finitePositiveNumber(payload?.latency?.providerEventTs)
      ?? candle.rawEpochMs,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    volume: candle.volume,
    latency: payload.latency && typeof payload.latency === 'object' ? payload.latency : null,
    lastPriceOnly: Array.isArray(payload.qualityFlags) && payload.qualityFlags.includes('trade-responsive'),
  }
}

export function equityStreamLastPriceToChartBar(payload, timeframe, timezone) {
  const barSize = equityStreamBarSizeForTimeframe(timeframe)
  const ibkrBarSize = IBKR_BAR_SIZE_BY_EQUITY_BAR_SIZE[barSize]
  if (!barSize || !ibkrBarSize) return null
  if (timeframe === 'daily') return equityStreamLastPriceToDailyChartBar(payload)

  const price = Number(payload?.price ?? payload?.close)
  if (!Number.isFinite(price) || price <= 0) return null
  const tradeTs = Number(payload?.tradeTs ?? payload?.eventTs)
  if (!Number.isFinite(tradeTs) || tradeTs <= 0) return null
  const barTime = alignEquityLastPriceBarTime(Math.floor(tradeTs / 1000), barSize)
  if (!Number.isFinite(barTime) || barTime <= 0) return null

  const rawBar = canonicalBarToIbkrRawBar({
    barTime,
    open: price,
    high: Number.isFinite(Number(payload.high)) ? Number(payload.high) : price,
    low: Number.isFinite(Number(payload.low)) ? Number(payload.low) : price,
    close: price,
    volume: null,
  }, ibkrBarSize)
  if (!rawBar) return null
  const candle = toCandles([rawBar], timezone)[0]
  if (!candle) return null

  return {
    time: candle.time,
    rawEpochMs: candle.rawEpochMs,
    eventEpochMs: tradeTs,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    volume: candle.volume,
    latency: payload.latency && typeof payload.latency === 'object' ? payload.latency : null,
    lastPriceOnly: true,
  }
}

function equityStreamLastPriceToDailyChartBar(payload) {
  const price = finitePositiveNumber(payload?.price ?? payload?.close)
  if (!price) return null
  const tradeTs = finitePositiveNumber(payload?.tradeTs ?? payload?.eventTs)
  if (!tradeTs) return null
  const time = newYorkDailyChartTime(Math.floor(tradeTs / 1000))
  if (!Number.isFinite(time) || time <= 0) return null

  return {
    time,
    rawEpochMs: time * 1000,
    eventEpochMs: tradeTs,
    open: price,
    high: price,
    low: price,
    close: price,
    volume: null,
    latency: payload.latency && typeof payload.latency === 'object' ? payload.latency : null,
    lastPriceOnly: true,
  }
}

function finitePositiveNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

export function alignEquityLastPriceBarTime(seconds, barSize) {
  const intervalSeconds = EQUITY_BAR_SIZE_SECONDS[barSize]
  if (!intervalSeconds) return null
  if (barSize === '1day') return newYorkDailyChartTime(seconds)
  if (barSize === '65min') {
    const open = regularSessionOpenForSeconds(seconds)
    return open + Math.floor((seconds - open) / intervalSeconds) * intervalSeconds
  }
  return seconds - positiveModulo(seconds, intervalSeconds)
}

function regularSessionOpenForSeconds(seconds) {
  const date = newYorkDateParts(seconds)
  return newYorkWallClockToUtcSeconds({ ...date, hour: 9, minute: 30 })
}

function newYorkDateParts(seconds) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(seconds * 1000))
  const get = (type) => parts.find((part) => part.type === type)?.value
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
  }
}

function newYorkDailyChartTime(seconds) {
  const { year, month, day } = newYorkDateParts(seconds)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  return Math.floor(Date.UTC(year, month - 1, day) / 1000)
}

function newYorkWallClockToUtcSeconds({ year, month, day, hour, minute }) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute) / 1000
  const offset = newYorkOffsetSeconds(utcGuess)
  return utcGuess - offset
}

function newYorkOffsetSeconds(seconds) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    timeZoneName: 'shortOffset',
    hour: '2-digit',
  }).formatToParts(new Date(seconds * 1000))
  const tz = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT-5'
  const match = tz.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/)
  if (!match) return -5 * 3600
  const hours = Number(match[1])
  const minutes = Number(match[2] || 0)
  return hours * 3600 + Math.sign(hours) * minutes * 60
}

function positiveModulo(value, modulo) {
  return ((value % modulo) + modulo) % modulo
}
